import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import spIcon from "../../assets/brand/swlogo.png";
import spWordmark from "../../assets/brand/swletters.png";
import api from "../../api/api";
import { toast } from "react-toastify";

export const SecurityPolicies = () => {
  const [pwd, setPwd] = useState("");
  const [cpwd, setCpwd] = useState("");
  const [twoFA, setTwoFA] = useState(true);
  const [accept, setAccept] = useState(false);
  const [saving, setSaving] = useState(false);      

  const navigate = useNavigate();

  const canSubmit = useMemo(
    () => pwd.trim() && cpwd.trim() && pwd === cpwd && accept,
    [pwd, cpwd, accept]
  );

  const onSubmit = async (e) => {
  e.preventDefault();
  if (!canSubmit) return;
  try {
    setSaving(true);
    await api.onboarding.saveSecurity({ twoFAEnabled: twoFA, termsAccepted: accept });
    navigate("/dashboard");
    } catch (e2) {
      toast.warn(e2?.data?.message || e2.message || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#fff8e1]">
      <div className="w-[412px] [font-family:'Nunito',Helvetica] text-[#5d4037]">

        {/* Centered logo */}
        <div className="w-full flex justify-center">
          <div className="inline-flex items-center gap-[0px] leading-none">
            <img
              src={spIcon}
              alt="Sweet Plate logo"
              className="shrink-0 select-none object-contain w-[65px] h-[86px] -mt-[2px]"
            />
            <img
              src={spWordmark}
              alt="Sweet Plate"
              className="shrink-0 select-none object-contain h-[29px] w-auto -ml-[4px]"
            />
          </div>
        </div>

        {/* Title */}
        <h1 className="mt-3 ml-6 font-bold text-[22px] leading-7">Join Sweet Plate Vendor</h1>

        {/* Stepper*/}
        <div className="w-[378px] mx-auto mt-3 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#5d4037] text-[#fff8e1] grid place-items-center text-sm font-bold">1</div>
          <div className="flex-1 h-[2px] bg-[#5d4037]" />
          <div className="w-8 h-8 rounded-full bg-[#5d4037] text-[#fff8e1] grid place-items-center text-sm font-bold">2</div>
          <div className="flex-1 h-[2px] bg-[#5d4037]" />
          <div className="w-8 h-8 rounded-full bg-[#5d4037] text-[#fff8e1] grid place-items-center text-sm font-bold">3</div>
          <div className="flex-1 h-[2px] bg-[#5d4037]/40" />
          <div className="w-8 h-8 rounded-full bg-[#5d4037] text-[#fff8e1] grid place-items-center text-sm font-bold">4</div>
        </div>
        
        {/* Section header */}
        <div className="w-[378px] mx-auto mt-6">
          <p className="font-extrabold uppercase text-[14px] tracking-wide">Security and Policies</p>
          <p className="mt-1 text-[14px]">
            Provide the basic information to get your business
            registered with us.
          </p>
        </div>

        <form onSubmit={onSubmit} className="mt-4">
          {/* Account Security */}
          <div className="w-[378px] mx-auto mt-4">
            <p className="text-base font-semibold mb-3">Account Security</p>

            <input
              type="password"
              placeholder="Create Password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="w-full h-[56px] bg-[#fff8e1] rounded-[18px] border border-[#5d4037] px-[17px] text-[#7a6f5c] text-base outline-none"
            />

            <input
              type="password"
              placeholder="Confirm Password"
              value={cpwd}
              onChange={(e) => setCpwd(e.target.value)}
              className="mt-4 w-full h-[56px] bg-[#fff8e1] rounded-[18px] border border-[#5d4037] px-[17px] text-[#7a6f5c] text-base outline-none"
            />
          </div>

          {/* Two-Factor Authentication */}
          <div className="w-[378px] mx-auto mt-6">
            <p className="text-base font-semibold mb-3">Two-Factor Authentication</p>

            <div className="w-full rounded-[14px] border border-[#5d4037] bg-[#fff8e1] px-4 py-3 flex items-center justify-between">
              <div>
                <div className="font-bold">Enable 2FA for your account</div>
                <div className="text-[13px] text-[#5d4037]/80">Recommended for account security</div>
              </div>

              {/* Toggle */}
              <button
                type="button"
                onClick={() => setTwoFA((v) => !v)}
                aria-label="Toggle 2FA"
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  twoFA ? "bg-[#5d4037]" : "bg-[#e8dcb7]"
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                    twoFA ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Terms & Policies */}
          <div className="w-[378px] mx-auto mt-6">
            <p className="text-base font-semibold mb-3">Terms & Policies</p>
            <label className="flex items-start gap-3 text-[14px] cursor-pointer">
              <input
                type="checkbox"
                checked={accept}
                onChange={(e) => setAccept(e.target.checked)}
                className="w-5 h-5 mt-0.5 rounded border border-[#5d4037] accent-[#5d4037]"
              />
              <span>I accept the Terms of Service &amp; Privacy Policy</span>
            </label>
          </div>

          {/* Complete Registration button */}
          <div className="w-[378px] mx-auto">
            <button
              type="submit"
              disabled={!canSubmit}
              className={`mt-7 mx-auto block w-[290px] h-[60px] rounded-[50px] bg-[#f7c945] font-bold text-[#5d4037] text-base leading-[22px] transition-opacity ${
                canSubmit ? "hover:opacity-90" : "opacity-50 cursor-not-allowed pointer-events-none"
              }`}
            >
              Complete Registration
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
